/**
 * UserShell — section layout for every user/* screen.
 *
 * Replaces app/user/_layout.tsx.  Wraps user screens with:
 *   - Auth guard (return null if not a user or logging out)
 *   - Suspended-account fallback (renders SuspendedBanner only)
 *   - Dynamic nav items: Attendant tab appears when the user has an
 *     attendant record, Fleet tab appears when the user is a fleet member
 */
import React, { ReactNode, useEffect, useState, useMemo } from 'react';
import { View } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useResponsive } from '../utils/responsive';
import { apiGet } from '../api/client';
import NavBanner, { NavItem } from '../components/NavBanner';
import SubHeader from '../components/SubHeader';
import SuspendedBanner from '../components/SuspendedBanner';
import ContentContainer from '../components/ContentContainer';
import GiftBanner from '../components/GiftBanner';
import VerifyEmailBanner from '../components/VerifyEmailBanner';

const baseItems: NavItem[] = [
  { icon: 'scan', label: 'Scan', route: 'index' },
  { icon: 'map', label: 'Map', route: 'map' },
  { icon: 'card', label: 'Payment', route: 'payment' },
  { icon: 'gift', label: 'Rewards', route: 'rewards' },
  { icon: 'receipt', label: 'History', route: 'history' },
];

export default function UserShell({ children }: { children: ReactNode }) {
  const { isAuthenticated, role, isLoading, isSuspended, isLoggingOut } = useAuth();
  const { colors } = useTheme();
  const { isDesktopWeb } = useResponsive();
  const [isAttendant, setIsAttendant] = useState(false);
  const [isFleetMember, setIsFleetMember] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    if (isAuthenticated && role === 'user' && !isSuspended && !isLoggingOut) {
      apiGet('/api/attendants/me')
        .then((r) => { if (!ac.signal.aborted) setIsAttendant(!!r); })
        .catch(() => { if (!ac.signal.aborted) setIsAttendant(false); });
      apiGet<{ length: number }[]>('/api/fleet/me/memberships')
        .then((r) => { if (!ac.signal.aborted) setIsFleetMember(r.length > 0); })
        .catch(() => { if (!ac.signal.aborted) setIsFleetMember(false); });
    }
    return () => ac.abort();
  }, [isAuthenticated, role, isSuspended, isLoggingOut]);

  // NOTE: keep ALL hooks above the early returns below. Calling useMemo after a
  // conditional `return null` skips them on logout (guard flips true), which
  // changes the hook count between renders and crashes with a Rules-of-Hooks
  // error → the global ErrorBoundary ("Something went wrong").
  const navItems = useMemo(() => {
    const items: NavItem[] = [...baseItems];
    items.push({ icon: 'ribbon', label: 'Membership', route: 'subscriptions' });
    if (isAttendant) items.push({ icon: 'time', label: 'Attendant', route: 'attendant' });
    if (isFleetMember) items.push({ icon: 'business', label: 'Fleet', route: 'fleet' });
    return items;
  }, [isAttendant, isFleetMember]);

  const mainRoutes = useMemo(
    () => [...navItems.map((i) => i.route), 'account'],
    [navItems],
  );

  if (!isLoading && (!isAuthenticated || role !== 'user' || isLoggingOut)) return null;

  if (isSuspended) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        {children}
        <SuspendedBanner />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isDesktopWeb ? 'row' : 'column',
        backgroundColor: colors.background,
      }}
    >
      <NavBanner
        items={navItems}
        rolePrefix="user"
        mainRoutes={mainRoutes}
        accountRoute="account"
        persistOnSubpages
      />
      <View style={{ flex: 1 }}>
        <GiftBanner />
        <VerifyEmailBanner />
        <SubHeader rolePrefix="user" mainRoutes={mainRoutes} />
        {/* footer=false: the shell wraps the screen's scroll view, so a footer
            here pins to the viewport bottom. Screens render PageFooter at
            their own scroll end (inner ContentContainer / ListFooterComponent). */}
        <ContentContainer role="user" footer={false}>{children}</ContentContainer>
      </View>
    </View>
  );
}
